import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listClassRosters } from '../../api/classRosterApi';
import { trackClassRosterEvent } from '../../utils/eventTracker';

const MISSING_PREVIEW = 30;

/** 제출방(교사 화면): 명단을 연결하면 낸 학생·안 낸 학생·명단에 없는 이름을 보여 준다. 학생 화면에는 쓰지 않는다. */
const RoomRosterStatus = ({ status, onLink }) => {
  const [rosters, setRosters] = useState(null);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const linked = Boolean(status);

  useEffect(() => {
    if (linked && !picking) return undefined;
    let cancelled = false;
    listClassRosters()
      .then(items => { if (!cancelled) setRosters(items); })
      .catch(() => { if (!cancelled) setRosters([]); });
    return () => { cancelled = true; };
  }, [linked, picking]);

  const link = async (rosterId) => {
    setSaving(true);
    try {
      if (!(await onLink?.(rosterId))) return;
      const picked = (rosters || []).find(item => item.id === rosterId);
      trackClassRosterEvent(rosterId ? 'room_roster_linked' : 'room_roster_unlinked', {
        surface: 'room',
        roster_count: (rosters || []).length,
        ...(picked ? { student_count: picked.student_count, roster_id: picked.id } : {}),
        ...(status ? { submitted_count: (status.submitted || []).length, missing_count: (status.missing || []).length } : {}),
      });
      setPicking(false);
    } finally {
      setSaving(false);
    }
  };

  if (!linked || picking) {
    return (
      <div className="room-roster" data-testid="room-roster-picker">
        <div className="room-roster-text">
          <strong>명단을 연결하면 안 낸 학생이 보여요</strong>
          <span>학생에게는 명단이 보이지 않아요.</span>
        </div>
        {rosters === null ? null : rosters.length === 0 ? (
          <Link className="room-roster-link" to="/classes">학급 관리에서 명단 만들기</Link>
        ) : (
          <div className="room-roster-options">
            {rosters.map(roster => (
              <button key={roster.id} type="button" disabled={saving} onClick={() => link(roster.id)}>
                {roster.label} · {roster.student_count}명
              </button>
            ))}
            {picking && (
              <button type="button" className="is-quiet" disabled={saving} onClick={() => setPicking(false)}>닫기</button>
            )}
          </div>
        )}
      </div>
    );
  }

  const { label, total, submitted = [], missing = [], unknown = [] } = status;
  const typos = submitted.filter(s => s.typed_name);
  return (
    <div className="room-roster is-linked" data-testid="room-roster-status">
      <div className="room-roster-head">
        <div className="room-roster-text">
          <strong>{label} · {total}명 중 {submitted.length}명 냈어요</strong>
          {missing.length === 0 && <span>모든 학생이 냈어요.</span>}
        </div>
        <div className="room-roster-head-actions">
          <button type="button" className="is-quiet" onClick={() => setPicking(true)}>명단 바꾸기</button>
          <button type="button" className="is-quiet" disabled={saving} onClick={() => link('')}>연결 끊기</button>
        </div>
      </div>
      {missing.length > 0 && (
        <div className="room-roster-group">
          <span className="room-roster-group-title">아직 안 낸 학생 {missing.length}명</span>
          <div className="room-roster-chips">
            {missing.slice(0, MISSING_PREVIEW).map(s => <span key={s.sid} className="room-roster-chip">{s.name}</span>)}
            {missing.length > MISSING_PREVIEW && <span className="room-roster-chip">외 {missing.length - MISSING_PREVIEW}명</span>}
          </div>
        </div>
      )}
      {typos.length > 0 && (
        <div className="room-roster-group">
          <span className="room-roster-group-title">이름을 다르게 적은 학생</span>
          <div className="room-roster-chips">
            {typos.map(s => <span key={s.sid} className="room-roster-chip">{s.typed_name} → {s.name}</span>)}
          </div>
        </div>
      )}
      {unknown.length > 0 && (
        <div className="room-roster-group">
          <span className="room-roster-group-title">명단에 없는 이름</span>
          <div className="room-roster-chips">
            {unknown.map(name => <span key={name} className="room-roster-chip is-warn">{name}</span>)}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomRosterStatus;
